import { faFilter } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useModel } from "@stem-sims/nexus"
import Paginator from "components/Dashboards/General/Pagination/Paginator"
import { ReactNode, useState } from "react"
import { Button, Col, Modal, Row } from "react-bootstrap"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import { deleteGradingPeriod, getGradingPeriods, updateGradingPeriod } from "shared/routes/teacher"
import { GradingPeriod } from "shared/types/teacherTypes"
import GradingPeriodForm from "./GradingPeriodForm"
import CustomModal from "components/Modals/CustomModal"

function sameContents(a: string[], b: string[]) {
  if (a.length !== b.length) return false

  const setA = new Set(a)
  const setB = new Set(b)

  if (setA.size !== setB.size) return false

  for (const item of setA) {
    if (!setB.has(item)) return false
  }
  return true
}

interface Props {
    classId: string
    selectedPeriods: string[]
    onSelect: (periodIds: string[]) => void
    refreshAssignments: () => void
}

const SelectGradingPeriods = ({ classId, selectedPeriods, onSelect, refreshAssignments } : Props) => {
    const [show, setShow] = useState(false)
    const [periods, setPeriods] = useState<string[]>(selectedPeriods || [])
    const [editingPeriod, setEditingPeriod] = useState<GradingPeriod>(null)
    const [viewingPeriod, setViewingPeriod] = useState<GradingPeriod>(null)
    const [deletingPeriod, setDeletingPeriod] = useState<GradingPeriod>(null)

    const { response: gradingPeriods, loading, refreshModel } = useModel({
        model: getGradingPeriods,
        props: { classId }
    })

    const onOpen = () => {
        setShow(true)
        refreshModel()
    }

    const onHide = () => {
        setShow(false)
    }

    const selectPeriod = (period: GradingPeriod) => {
      if (periods.includes(period.id)) {
          setPeriods(prev => prev.filter(id => id !== period.id))
      } else {
          setPeriods(prev => [...prev, period.id])
      }
    }

    const deletePeriod = (e: any, period: GradingPeriod) => {
      e.stopPropagation()
      setShow(false)
      setDeletingPeriod(period)
    }

    const onSubmit = () => {
      onSelect(periods)
      onHide()
    }

    const renderPeriodItem = (period: GradingPeriod, index: number) : ReactNode  => {
      const isSelected = periods.includes(period.id)
      return (
        <>
          <li 
            role="button" 
            className={`list-group-item ${isSelected ? 'bg-primary text-white' : ''}`} 
            key={index} 
            onClick={() => selectPeriod(period)}>
              <Row>
                  <Col xs={5} className="text-start">
                    <span className="fw-bold me-3">{period.name}</span>
                  </Col>
                  <Col xs={7} className="text-start">
                    <span className="float-end">
                      <i 
                        className={`fas fa-glasses ${isSelected ? 'text-white' : 'text-primary'} me-2`}
                        onClick={(e) => {
                            e.stopPropagation()
                            setViewingPeriod(prev => prev?.id === period?.id ? null : period)
                          }} />
                      <i 
                        className={`fa-regular fa-pen-to-square ${isSelected ? 'text-white' : 'text-primary'} me-2`} 
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingPeriod(prev => prev?.id === period?.id ? null : period)
                        }} />
                      <i className={`fa-regular fa-x ${isSelected ? 'text-white' : 'text-danger'} `} onClick={(e) => deletePeriod(e, period)} />
                    </span>
                  </Col>
              </Row>
          </li>
        </>
      )
    }

    if (loading || !gradingPeriods) return <BeatLoader />

  return (
    <div>
        <Button onClick={onOpen}> <FontAwesomeIcon icon={faFilter} /> Select Grading Periods</Button>
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header className="border-0" closeButton>
              <h3 className="w-100 text-center"> <FontAwesomeIcon icon={faFilter} /> Select Grading Period</h3>
            </Modal.Header>
            <Modal.Body>
              <p className="text-start text-muted fw-bold">Filter the gradebook by selecting Grading Periods to see only assignments and grades which are included in the selected Grading Periods.</p>
              {gradingPeriods.length === 0 && <h5>No grading periods available. Please create grading periods first.</h5>}
              <Paginator items={gradingPeriods} renderItem={renderPeriodItem} itemsPerPage={10} position="up" />
              <Button 
                variant="primary" 
                className="w-50 mt-3"
                disabled={sameContents(periods, selectedPeriods)}
                onClick={onSubmit}>
                  Apply Selection
              </Button>
            </Modal.Body>
        </Modal>
        <GradingPeriodForm 
          type="edit"
          classId={classId} 
          show={!!editingPeriod} 
          period={editingPeriod}
          onHide={() => setEditingPeriod(null)} 
          onSubmit={async (periodName: string, assignmentIds: string[], classId: string) => {
            updateGradingPeriod({ id: editingPeriod.id, name: periodName, classId: classId, assignmentIds: assignmentIds })
              .then(() => {
                  toast.success("Grading period updated successfully.")
                  setEditingPeriod(null)
                  refreshModel()
                  onSelect(periods)

                  if (selectedPeriods.includes(editingPeriod.id)) {
                    refreshAssignments()
                  }
              })
              .catch(err => {
                  toast.error(err.response?.data?.message ?? "Failed to update grading period.")
              })
          }} />

          <GradingPeriodForm 
            type="view"
            classId={classId} 
            show={!!viewingPeriod} 
            period={viewingPeriod}
            onHide={() => setViewingPeriod(null)} 
            onSubmit={null} />

          <CustomModal
            showModal={!!deletingPeriod}
            header="Confirm Deleting Grading Period"
            body={`Are you sure that you want to delete ${deletingPeriod?.name}?`}
            onConfirm={() => {
              if (!deletingPeriod) return
              deleteGradingPeriod({ classId, id: deletingPeriod.id })
                .then(() => {
                  const newPeriods = periods.filter(id => id !== deletingPeriod.id)
                  setPeriods(newPeriods)
                  toast.success("Grading period deleted successfully.")
                  refreshModel()

                  if (selectedPeriods.includes(deletingPeriod.id)) {
                    onSelect(newPeriods)
                  }
                })
                .catch(err => {
                  toast.error(err.response?.data?.message ?? "Failed to delete grading period.")
                })
                .finally(() => {
                  setDeletingPeriod(null)
                  setShow(true)
                })
            }}
            onDecline={() => {
              setDeletingPeriod(null)
              setShow(true)
            }}
            confirmBtn={{text: "Confirm", color: "danger"}}
            declineBtn={{text: "Cancel", color: "theme"}}
           />

    </div>
  )
}

export default SelectGradingPeriods
